import React from "react";
import EmployeeProfileTabHeader from "./EmployeeProfileTabHeader";
import EmployeeProfileSectionCard from "./EmployeeProfileSectionCard";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import Drawer from "@mui/material/Drawer";
import DrawerHeader from "../../shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../../shared/helpers/DrawerFooterContainerLarge";
import ButtonOutlined from "../../shared/helpers/ButtonOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Switch from "@mui/material/Switch";
import TimeOffMineTable from "../../shared/availability/TimeOffMineTable";

function EmployeeProfileTimeOffTab(props) {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [modalData, setModalData] = React.useState({
    can_request_pto: props.selectedTeamMemberData?.can_request_pto ?? false,
    can_request_sicktime: props.selectedTeamMemberData?.can_request_sicktime ?? false,
  });

  React.useEffect(() => {
    setModalData({
      can_request_pto: props.selectedTeamMemberData?.can_request_pto ?? false,
      can_request_sicktime: props.selectedTeamMemberData?.can_request_sicktime ?? false,
    });
  }, [props.selectedTeamMemberData?.can_request_pto, props.selectedTeamMemberData?.can_request_sicktime]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setModalData({
      can_request_pto: props.selectedTeamMemberData?.can_request_pto ?? false,
      can_request_sicktime: props.selectedTeamMemberData?.can_request_sicktime ?? false,
    });
    setOpen(false);
  };

  const handleTogglePto = () => {
    setModalData((prev) => ({
      ...prev,
      can_request_pto: !prev.can_request_pto,
    }));
  };

  const handleToggleSicktime = () => {
    setModalData((prev) => ({
      ...prev,
      can_request_sicktime: !prev.can_request_sicktime,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await props.post_store_team_member(
        props.selectedTeamMemberData?.first_name,
        props.selectedTeamMemberData?.last_name,
        props.selectedTeamMemberData?.email,
        props.selectedTeamMemberData?.phone_number,
        props.selectedTeamMemberData?.permission_type,
        props.selectedTeamMemberData?.id,
        props.selectedTeamMemberData?.clock_pin,
        props.selectedTeamMemberData?.date_of_birth,
        props.selectedTeamMemberData?.date_of_hire,
        false, // send_welcome_email
        null, // available_role_info
        modalData.can_request_pto,
        modalData.can_request_sicktime
      );
      handleClose();
    } finally {
      setSaving(false);
    }
  };

  const canRequestPto = props.selectedTeamMemberData?.can_request_pto ?? false;
  const canRequestSicktime = props.selectedTeamMemberData?.can_request_sicktime ?? false;

  const handleRequestTimeOff = (data) => {
    if (!props.request_time_off_for_employee) return;
    return props.request_time_off_for_employee({
      ...data,
      stm_id: props.selectedTeamMemberData?.id,
    });
  };

  const timeOffTableRef = React.useRef(null);

  const renderSettingChip = (label, enabled) => (
    <span
      title={
        enabled
          ? `Can request ${label.toLowerCase()}`
          : `Cannot request ${label.toLowerCase()}`
      }
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${
        enabled
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-gray-50 text-gray-600 border-gray-200"
      }`}
    >
      {enabled ? (
        <CheckCircleIcon style={{ fontSize: "15px" }} />
      ) : (
        <CancelIcon style={{ fontSize: "15px" }} />
      )}
      {label}
    </span>
  );

  return (
    <>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <EmployeeProfileTabHeader
          icon={EventBusyIcon}
          title="Time Off"
          subtitle="PTO and sick time settings and requests"
          actionLabel="Add Time Off"
          actionIcon={AddIcon}
          onAction={() => timeOffTableRef.current?.openAddDrawer()}
        />

        {/* Time Off — settings strip + requests table */}
        <EmployeeProfileSectionCard bodyClassName="">
          {/* Settings strip */}
          <div className="flex items-center justify-between gap-3 px-6 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mr-1">
                Requests Allowed
              </span>
              {renderSettingChip("PTO", canRequestPto)}
              {renderSettingChip("Sick Time", canRequestSicktime)}
            </div>
            <button
              onClick={handleOpen}
              className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-gray-500 hover:text-violet-700 transition-colors"
            >
              <EditIcon style={{ fontSize: "14px" }} />
              <span>Edit settings</span>
            </button>
          </div>

          {/* Requests Table */}
          <div className="pb-6 [&>div]:!mt-0">
            <TimeOffMineTable
              ref={timeOffTableRef}
              hideHeader
              myTimeOffData={props.selectedTeamMemberTimeOffData ?? []}
              request_time_off={handleRequestTimeOff}
              rescind_time_off_request={props.rescind_time_off_request}
              minEffectiveDate={new Date()}
              blackoutDates={[]}
              canRequestPto={canRequestPto}
              canRequestSicktime={canRequestSicktime}
              storeTimezone={props.storeTimezone}
            />
          </div>
        </EmployeeProfileSectionCard>
      </div>

      {/* Edit Drawer */}
      <Drawer open={open} anchor="right" onClose={handleClose}>
        <div className="w-[450px] h-full">
          <div className="max-h-screen overflow-auto">
            <DrawerHeader
              title="Edit Time Off Settings"
              onClose={handleClose}
            />
            <div className="w-full px-6">
              {/* PTO Toggle */}
              <div className="mt-5">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Can Request PTO</p>
                    <p className="text-xs text-gray-500 mt-1">Allow paid time off requests</p>
                  </div>
                  <Switch
                    checked={modalData.can_request_pto}
                    onChange={handleTogglePto}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#6d28d9',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#6d28d9',
                      },
                    }}
                  />
                </div>
              </div>

              {/* Sick Time Toggle */}
              <div className="mt-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Can Request Sick Time</p>
                    <p className="text-xs text-gray-500 mt-1">Allow sick time requests</p>
                  </div>
                  <Switch
                    checked={modalData.can_request_sicktime}
                    onChange={handleToggleSicktime}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#6d28d9',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#6d28d9',
                      },
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="h-[400px]"></div>
          </div>
          <div>
            <DrawerFooterContainerLarge>
              <div className="w-full">
                <LoadingButton
                  variant="contained"
                  loading={saving}
                  loadingPosition="end"
                  endIcon={<SaveIcon />}
                  onClick={handleSave}
                  sx={{
                    width: "100%",
                    textTransform: "none",
                    borderRadius: "12px",
                    paddingY: "14px",
                    backgroundColor: "#6d28d9",
                    "&:hover": {
                      backgroundColor: "#5b21b6",
                    },
                    fontSize: "16px",
                  }}
                >
                  Save Changes
                </LoadingButton>
              </div>
              <div className="w-full mt-3">
                <ButtonOutlined text="Cancel" onClick={handleClose} />
              </div>
            </DrawerFooterContainerLarge>
          </div>
        </div>
      </Drawer>
    </>
  );
}

export default EmployeeProfileTimeOffTab;
